import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, Shield, Database, Eye, Lock, 
  Share2, Settings, Mail, Globe, Trash2
} from 'lucide-react';

const PrivacyPage = () => {
  const lastUpdated = '15 de abril de 2026';

  const sections = [
    {
      id: 'intro',
      icon: Shield,
      title: '1. Introdução',
      content: `
        A sua privacidade é importante para a gente!

        Esta política explica como a hello, social coleta, usa e protege os dados que você fornece ao acessar nosso site e utilizar nossos serviços.

        Podemos atualizar esta política a qualquer momento. Sempre que isso acontecer, você será informado. Para se manter por dentro, recomendamos revisar esta página periodicamente.

        Ao usar nossos serviços, você concorda com a coleta e uso de informações de acordo com esta política. Processamos seus dados de acordo com a Lei Geral de Proteção de Dados (LGPD - Lei nº 13.709/2018).
      `
    },
    {
      id: 'collection',
      icon: Database,
      title: '2. Dados que Coletamos',
      content: `
        Ao navegar em nosso site e utilizar nossos serviços, podemos coletar algumas informações, como:
        • Endereço IP
        • Informações de contato (como e-mail)
        • Dados sobre seus interesses e preferências
        • Informações sobre sua navegação e comportamento dentro do site

        Abaixo detalhamos por categoria:

        2.1 Informações que você fornece:
        • Dados de cadastro: nome, e-mail, senha (criptografada)
        • Informações da marca: nome da empresa, descrição, cores, logotipo
        • Conteúdo: prompts de texto, preferências de estilo, histórico de criações

        2.2 Dados coletados automaticamente:
        • Informações do dispositivo: tipo, sistema operacional, navegador
        • Dados de uso: páginas visitadas, funcionalidades utilizadas, tempo de sessão
        • Endereço IP e localização aproximada
        • Cookies e tecnologias similares

        2.3 Dados de redes sociais (quando conectadas):
        • Nome de usuário e ID do perfil
        • Foto de perfil (opcional)
        • Token de acesso OAuth (criptografado)
        • NÃO coletamos: senhas, mensagens privadas, lista de seguidores
      `
    },
    {
      id: 'usage',
      icon: Eye,
      title: '3. Como Usamos Seus Dados',
      content: `
        Usamos essas informações para melhorar sua experiência e nossos serviços. Isso inclui:

        • Entender melhor o que você precisa
        • Evoluir nossos produtos e funcionalidades
        • Enviar comunicações e novidades que façam sentido para você
        • Realizar pesquisas e entender melhor nosso público
        • Personalizar sua experiência dentro do site
        • Gerar conteúdo personalizado com IA baseado nas suas preferências
        • Conectar e publicar em suas redes sociais autorizadas
        • Processar pagamentos e gerenciar assinaturas
        • Enviar comunicações importantes sobre sua conta
        • Detectar e prevenir fraudes ou uso indevido
        • Cumprir obrigações legais

        NÃO vendemos, alugamos ou compartilhamos seus dados pessoais com terceiros para fins de marketing.
      `
    },
    {
      id: 'ai',
      icon: Globe,
      title: '4. Inteligência Artificial e Geração de Conteúdo',
      content: `
        4.1 Como funciona:
        Utilizamos modelos de IA generativa para criar imagens e vídeos baseados nos seus prompts.
        Os prompts que você fornece são processados para gerar conteúdo único.

        4.2 Treinamento de IA:
        • Seus dados NÃO são usados para treinar nossos modelos de IA
        • Utilizamos modelos de terceiros (OpenAI, Google, etc.) que possuem suas próprias políticas
        • Os prompts são processados em tempo real e não são retidos para treinamento

        4.3 Armazenamento de conteúdo gerado:
        • Imagens e vídeos criados são armazenados na sua conta
        • Você pode excluir seu conteúdo a qualquer momento
        • Conteúdo é automaticamente excluído após 90 dias de inatividade da conta
      `
    },
    {
      id: 'social',
      icon: Share2,
      title: '5. Conexões com Redes Sociais',
      content: `
        5.1 Autenticação OAuth 2.0:
        Quando você conecta Instagram, TikTok ou outras redes, utilizamos OAuth 2.0, 
        um protocolo seguro de autorização que:
        • Nunca nos dá acesso à sua senha
        • Permite que você revogue o acesso a qualquer momento
        • Limita nosso acesso apenas às permissões autorizadas

        5.2 Dados acessados:
        • Informações públicas do perfil
        • Permissão para publicar conteúdo em seu nome
        • Capacidade de agendar publicações

        5.3 O que NÃO acessamos:
        • Suas mensagens privadas (DMs)
        • Sua lista de seguidores/seguidos
        • Dados de outros usuários
        • Informações de pagamento das plataformas

        5.4 Revogação:
        Você pode desconectar suas redes sociais a qualquer momento nas configurações da conta.
        Isso remove imediatamente nosso acesso à sua conta naquela plataforma.
      `
    },
    {
      id: 'storage',
      icon: Lock,
      title: '6. Armazenamento e Segurança',
      content: `
        6.1 Onde armazenamos:
        Seus dados são armazenados em servidores seguros na nuvem (Microsoft Azure, AWS).
        Os servidores estão localizados no Brasil e Estados Unidos, em conformidade com a LGPD.

        6.2 Medidas de segurança:
        • Criptografia em trânsito (HTTPS/TLS)
        • Criptografia em repouso (AES-256)
        • Senhas armazenadas com hash bcrypt
        • Tokens OAuth criptografados
        • Backups automáticos diários
        • Monitoramento 24/7 de segurança
        • Acesso restrito por autenticação multifator

        6.3 Retenção de dados:
        • Dados da conta: enquanto a conta estiver ativa
        • Conteúdo gerado: até você excluir ou 90 dias após inatividade
        • Logs de acesso: 12 meses
        • Dados de pagamento: conforme exigências legais (5 anos)
      `
    },
    {
      id: 'sharing',
      icon: Share2,
      title: '7. Compartilhamento de Dados',
      content: `
        Compartilhamos dados apenas nas seguintes situações:

        7.1 Prestadores de serviço:
        • Processadores de pagamento (Stripe) - dados de cobrança
        • Provedores de IA (OpenAI, Google) - prompts para geração
        • Serviços de hospedagem (Azure, AWS) - armazenamento
        • Ferramentas de análise (anônimas)

        7.2 Obrigações legais:
        Podemos divulgar dados se exigido por lei, ordem judicial ou autoridade competente.

        7.3 Proteções:
        • Todos os prestadores assinam acordos de proteção de dados
        • Compartilhamos apenas o mínimo necessário
        • Provedores de IA não retêm seus prompts após processamento
      `
    },
    {
      id: 'rights',
      icon: Settings,
      title: '8. Seus Direitos (LGPD)',
      content: `
        Conforme a Lei Geral de Proteção de Dados, você tem direito a:

        • Confirmação: saber se tratamos seus dados
        • Acesso: obter cópia dos dados que temos sobre você
        • Correção: corrigir dados incompletos ou incorretos
        • Anonimização/bloqueio: solicitar anonimização de dados desnecessários
        • Portabilidade: receber seus dados em formato estruturado
        • Eliminação: solicitar exclusão dos seus dados pessoais
        • Informação: saber com quem compartilhamos seus dados
        • Revogação: retirar consentimento a qualquer momento
        • Oposição: opor-se a tratamentos específicos

        Para exercer seus direitos, entre em contato pelo e-mail: privacidade@hellosocial.com.br
        Responderemos em até 15 dias úteis.
      `
    },
    {
      id: 'cookies',
      icon: Database,
      title: '9. Cookies e Tecnologias de Rastreamento',
      content: `
        9.1 O que são cookies:
        Cookies são pequenos arquivos de texto armazenados no seu navegador.

        9.2 Cookies que utilizamos:
        • Essenciais: necessários para funcionamento (autenticação, sessão)
        • Funcionais: lembram suas preferências (idioma, tema)
        • Analíticos: nos ajudam a entender o uso da plataforma (anônimos)

        9.3 Cookies de terceiros:
        • Stripe: processamento de pagamentos
        • Google Analytics: análise de uso (anonimizado)

        9.4 Gerenciamento:
        Você pode configurar seu navegador para recusar cookies, mas algumas funcionalidades podem não funcionar corretamente.
      `
    },
    {
      id: 'children',
      icon: Shield,
      title: '10. Menores de Idade',
      content: `
        Nossos serviços não são destinados a menores de 18 anos.
        Não coletamos intencionalmente dados de menores.

        Se você é pai/responsável e acredita que seu filho forneceu dados pessoais, 
        entre em contato imediatamente para que possamos excluí-los.
      `
    },
    {
      id: 'changes',
      icon: Mail,
      title: '11. Alterações nesta Política',
      content: `
        Podemos atualizar esta política a qualquer momento. Sempre que isso acontecer, você será informado.

        Alterações significativas serão comunicadas por:

        • E-mail para usuários registrados
        • Notificação na plataforma
        • Aviso destacado no site

        Para se manter por dentro, recomendamos revisar esta página periodicamente.
        A data da última atualização está sempre no topo desta página.
      `
    },
    {
      id: 'contact',
      icon: Mail,
      title: '12. Contato e DPO',
      content: `
        Para questões sobre privacidade ou exercer seus direitos:

        E-mail: privacidade@hellosocial.com.br
        
        Encarregado de Proteção de Dados (DPO):
        dpo@hellosocial.com.br

        Endereço:
        São Paulo, SP - Brasil

        Responderemos em até 15 dias úteis.

        Se não estiver satisfeito com nossa resposta, você pode reclamar à 
        Autoridade Nacional de Proteção de Dados (ANPD).
      `
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link 
              to="/" 
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Voltar</span>
            </Link>
            
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo.png" alt="hello, social" className="w-12 h-12 object-contain" />
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 px-4 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-green-100 to-emerald-100 rounded-2xl flex items-center justify-center">
            <Shield className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Política de Privacidade
          </h1>
          <p className="text-gray-600 mb-2">
            Última atualização: {lastUpdated}
          </p>
          <p className="text-sm text-gray-500">
            Em conformidade com a LGPD (Lei nº 13.709/2018)
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Highlights */}
          <div className="grid sm:grid-cols-3 gap-4 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-gray-200 text-center">
              <div className="w-12 h-12 mx-auto mb-3 bg-green-100 rounded-xl flex items-center justify-center">
                <Lock className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-1">Dados Criptografados</h3>
              <p className="text-sm text-gray-500">AES-256 em repouso e TLS em trânsito</p>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-gray-200 text-center">
              <div className="w-12 h-12 mx-auto mb-3 bg-blue-100 rounded-xl flex items-center justify-center">
                <Shield className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-1">OAuth Seguro</h3>
              <p className="text-sm text-gray-500">Nunca acessamos suas senhas</p>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-gray-200 text-center">
              <div className="w-12 h-12 mx-auto mb-3 bg-purple-100 rounded-xl flex items-center justify-center">
                <Trash2 className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-1">Seus Dados, Seu Controle</h3>
              <p className="text-sm text-gray-500">Exclua quando quiser</p>
            </div>
          </div>

          {/* Intro Alert */}
          <div className="bg-green-50 border border-green-200 rounded-2xl p-6 mb-8">
            <p className="text-green-800">
              <strong>Nosso compromisso:</strong> Seus dados são tratados com respeito e segurança. 
              Não vendemos informações pessoais e você tem total controle sobre seus dados.
            </p>
          </div>

          {/* Table of Contents */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-8">
            <h2 className="font-bold text-gray-900 mb-4">Índice</h2>
            <div className="grid sm:grid-cols-2 gap-2">
              {sections.map((section) => (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  className="text-sm text-gray-600 hover:text-green-600 transition-colors"
                >
                  {section.title}
                </a>
              ))}
            </div>
          </div>

          {/* Sections */}
          <div className="space-y-8">
            {sections.map((section) => {
              const Icon = section.icon;
              return (
                <div
                  key={section.id}
                  id={section.id}
                  className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 scroll-mt-24"
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-gray-600" />
                    </div>
                    <h2 className="text-xl font-bold text-gray-900">{section.title}</h2>
                  </div>
                  <div className="prose prose-gray max-w-none">
                    {section.content.split('\n').map((paragraph, idx) => (
                      <p key={idx} className="text-gray-600 mb-3 whitespace-pre-line">
                        {paragraph.trim()}
                      </p>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer Note */}
          <div className="mt-12 text-center">
            <p className="text-gray-500 text-sm mb-4">
              Sua privacidade é importante para nós. Se tiver dúvidas, entre em contato.
            </p>
            <div className="flex items-center justify-center gap-4">
              <Link to="/terms" className="text-green-600 hover:text-green-700 font-medium text-sm">
                Termos de Uso →
              </Link>
              <a 
                href="mailto:privacidade@hellosocial.com.br" 
                className="text-green-600 hover:text-green-700 font-medium text-sm"
              >
                Falar com DPO →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 bg-white border-t border-gray-100">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="hello, social" className="w-8 h-8 object-contain" />
            <span className="font-semibold text-gray-900">hello, social</span>
          </div>
          
          <p className="text-sm text-gray-500">
            © 2026 hello, social. Todos os direitos reservados.
          </p>

          <div className="flex items-center gap-6">
            <Link to="/terms" className="text-sm text-gray-500 hover:text-gray-700 transition-colors">
              Termos
            </Link>
            <Link to="/privacy" className="text-sm text-green-600 font-medium">
              Privacidade
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPage;